/**
 * Authentication Methods Hook
 * Handles different authentication flows (OIDC, Token Exchange, Manual, TPass)
 */

import { message } from "antd";
import { useCallback } from "react";
import { useAuth as useOidcAuth } from "react-oidc-context";

import { EnvVariables } from "@/env-variables";
import { generateChallenge, generateCodeVerifier } from "@/lib/auth/pkce";
import * as storage from "@/lib/storage";
import * as LocalStorage from "@/storages/LocalStorage";
import { useAdditionalInfo } from "@/stores/domains/additionalInfo";
import { Logger } from "@/utils";
import { SSO_AUTH_PROGRESS_KEY } from "../constants";
import { createAuthReturnState, requireAdditionalInfoOnNextLogin } from "../redirect";
import type { User } from "../types";

interface UseAuthMethodsProps {
  saveUserSession: (userData: User) => Promise<void>;
  handlePostLoginInitialization: () => Promise<void>;
  getRedirectPath: () => string;
  safeRedirect: (path: string) => void;
}

interface UseAuthMethodsReturn {
  handleTokenExchange: (username: string, sessionId: string, iv: string) => Promise<boolean>;
  handleOidcLogin: (
    accessToken?: string,
    refreshToken?: string,
  ) => Promise<{ userData: User; shouldRedirect: boolean }>;
  handleManualLogin: (userData: User, token: string, refreshToken?: string) => void;
  handleTpassLogin: (clientId: string, redirectUri: string) => Promise<void>;
}

const requireFreshAdditionalInfo = () => {
  requireAdditionalInfoOnNextLogin(undefined, { force: true });
  useAdditionalInfo.getState().reset();
};

/**
 * Custom hook for authentication methods
 */
export const useAuthMethods = ({
  saveUserSession,
  handlePostLoginInitialization,
  getRedirectPath,
  safeRedirect,
}: UseAuthMethodsProps): UseAuthMethodsReturn => {
  const oidcAuth = useOidcAuth();

  /**
   * Handle token exchange flow
   */
  const handleTokenExchange = useCallback(
    async (username: string, sessionId: string, iv: string): Promise<boolean> => {
      try {
        // Ensure code_verifier exists, generate if missing
        let codeVerifier = storage.sessionStore.safeGet(storage.CODE_VERIFIER_KEY);
        if (!codeVerifier) {
          codeVerifier = generateCodeVerifier();
          storage.sessionStore.safeSet(storage.CODE_VERIFIER_KEY, codeVerifier);
          Logger.warn(
            "code_verifier was missing during token exchange, generated new one. This may indicate a timing issue.",
          );
        }

        const response = await fetch(EnvVariables.tokenExchangeUrl, {
          method: "POST",
          body: JSON.stringify({
            username,
            session_id: sessionId,
            client_id: EnvVariables.clientId,
            iv_raw: iv,
            code_verifier: codeVerifier,
          }),
        });

        const data = await response.json();

        if (data.AuthenticationResult) {
          // Store tokens
          LocalStorage.setAccessToken(data.AuthenticationResult.AccessToken);
          if (data.AuthenticationResult.RefreshToken) {
            LocalStorage.setRefreshToken(data.AuthenticationResult.RefreshToken);
          }

          // Set initialization flag BEFORE saving user session to block API requests
          // This ensures session/register is called before any other API requests
          LocalStorage.setItem("session_initializing", "true");
          requireFreshAdditionalInfo();

          const userData: User = {
            id: username,
            username,
            name: username,
            email: username,
            avatar: "",
          };

          await saveUserSession(userData);

          // Register session ID first; clinic context and socket wait for AdditionalInfo.
          try {
            await handlePostLoginInitialization();
            const targetPath = getRedirectPath();
            sessionStorage.removeItem(SSO_AUTH_PROGRESS_KEY);
            safeRedirect(targetPath);
          } catch (initError) {
            Logger.error("Post-login initialization failed:", initError);
            message.error("Login initialization failed. Some features may not work properly.");
            // Still redirect to avoid being stuck
            const targetPath = getRedirectPath();
            sessionStorage.removeItem(SSO_AUTH_PROGRESS_KEY);
            safeRedirect(targetPath);
          }
          return true;
        }
        return false;
      } catch (error) {
        Logger.error("Token exchange failed:", error);
        message.error(
          `Authentication failed: ${error instanceof Error ? error.message : "Unknown error"}`,
        );
        return false;
      }
    },
    [saveUserSession, handlePostLoginInitialization, getRedirectPath, safeRedirect],
  );

  /**
   * Handle OIDC login flow
   */
  const handleOidcLogin = useCallback(
    async (
      accessToken?: string,
      refreshToken?: string,
    ): Promise<{ userData: User; shouldRedirect: boolean }> => {
      const user = oidcAuth.user;
      if (!user) {
        throw new Error("OIDC user not available");
      }

      // Set initialization flag BEFORE saving user session to block API requests
      // This ensures session/register is called before any other API requests
      LocalStorage.setItem("session_initializing", "true");

      const userData: User = {
        id: user.profile.sub || "",
        username: user.profile.username as string,
        name: user.profile.name || "",
        email: user.profile.email || "",
        avatar: "",
      };

      if (accessToken) {
        LocalStorage.setAccessToken(accessToken);
      } else {
        throw new Error("OIDC access token not available");
      }

      if (refreshToken) {
        LocalStorage.setRefreshToken(refreshToken);
      }

      requireFreshAdditionalInfo();
      await saveUserSession(userData);

      // Register session ID first; clinic context and socket wait for AdditionalInfo.
      try {
        await handlePostLoginInitialization();
        Logger.info("Session initialized successfully after OIDC login");
      } catch (error) {
        Logger.error("Failed to initialize session after OIDC login:", error);
        message.error("Initialization failed. Some features may not work properly.");
      }

      return { userData, shouldRedirect: true };
    },
    [oidcAuth.user, saveUserSession, handlePostLoginInitialization],
  );

  /**
   * Handle manual login flow
   */
  const handleManualLogin = useCallback(
    (userData: User, token: string, refreshToken?: string) => {
      LocalStorage.setAccessToken(token);
      if (refreshToken) {
        LocalStorage.setRefreshToken(refreshToken);
      }

      // Set initialization flag BEFORE saving user session to block API requests
      // This ensures session/register is called before any other API requests
      LocalStorage.setItem("session_initializing", "true");

      requireFreshAdditionalInfo();

      saveUserSession(userData)
        .then(() => handlePostLoginInitialization())
        .then(() => {
          Logger.info("Session initialized successfully after manual login");
          const targetPath = getRedirectPath();
          safeRedirect(targetPath);
        })
        .catch((error) => {
          Logger.error("Error during post-login process:", error);
          message.error("Login initialization failed. Some features may not work properly.");
          // Still redirect even if initialization fails
          const targetPath = getRedirectPath();
          safeRedirect(targetPath);
        });
    },
    [saveUserSession, handlePostLoginInitialization, getRedirectPath, safeRedirect],
  );

  /**
   * Handle Thomson Pass login flow
   */
  const handleTpassLogin = useCallback(
    async (clientId: string, redirectUri: string): Promise<void> => {
      const codeVerifier =
        storage.sessionStore.safeGet(storage.CODE_VERIFIER_KEY) || generateCodeVerifier();
      storage.sessionStore.safeSet(storage.CODE_VERIFIER_KEY, codeVerifier);

      try {
        const codeChallenge = await generateChallenge(codeVerifier);
        const authState = createAuthReturnState();
        const stateParam = authState ? `&state=${encodeURIComponent(authState)}` : "";

        const loginUrl = `${EnvVariables.tpassLoginUrl}?redirect_uri=${encodeURIComponent(redirectUri)}&code_challenge=${encodeURIComponent(codeChallenge)}&client_id=${encodeURIComponent(clientId)}${stateParam}`;

        window.location.href = loginUrl;
      } catch (error) {
        console.error("❌ [handleTpassLogin] Error:", error);
        Logger.error("Thomson Pass login failed:", error);
        message.error(`Login failed: ${error instanceof Error ? error.message : "Unknown error"}`);
      }
    },
    [],
  );

  return {
    handleTokenExchange,
    handleOidcLogin,
    handleManualLogin,
    handleTpassLogin,
  };
};
